import { ChatOptionsMenu } from '@/app/_common/chat-options-menu'
import { RerunIcon } from '@/components/icons/rerun-icon'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import { DEFAULT_PAGINATION_OPTIONS, PaginationControls } from '@/components/ui/pagination-controls'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import {
  isWorkflowStatusLoading,
  useAllWorkflowsQuery,
  UseAllWorkflowsSearchParams,
  WorkflowStatus,
} from '@/hooks/api/use-all-workflows-query'
import { usePersistentPagination } from '@/hooks/use-persistent-pagination'
import { usePersistentSorting } from '@/hooks/use-persistent-sorting'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { T, useTranslate } from '@tolgee/react'
import dayjs from 'dayjs'
import { CircleAlertIcon, CircleCheckIcon, CircleDashedIcon, XCircleIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import { PageTemplate } from '../_common/page-template'
import { usePromptLocalStorage } from '../_common/prompt-form'
import { TabsPanelId, useSplitView } from '../_common/split-view/split-view-context'
import { createStaticTab, createWorkflowTab } from '../_common/split-view/tabs/factories'
import { TAB_TYPE_CHAT, TabTypeStatic } from '../_common/split-view/tabs/types'
import ScheduleCard from './schedule-card'
import { CHStatusColumnFilter } from './_components/status-column-filter'
import { usePersistentColumnFilters } from '@/hooks/use-persistent-filtering'

export type ChatHistoryTableDef = {
  options?: string
  workflowId: string
  onOpenWorkflow: () => void
  onRerunWorkflow: () => void
  title: string | null
  recentTurnStartTimeEpoch: number
  status: WorkflowStatus
  prompt: string
  pinned?: boolean | null
  scheduleId?: string
}

export const HISTORY_TABLE_SORTING_KEY = 'history-table-sorting'
export const HISTORY_TABLE_PAGINATION_KEY = 'history-table-pagination'

const CHAT_HISTORY_COLUMN_DEFS: ColumnDef<ChatHistoryTableDef>[] = [
  {
    accessorKey: 'title',
    enableColumnFilter: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='history.workflowsTable.headers.title'>
        <T keyName='history.workflowsTable.headers.title' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell, row }) => {
      const { t } = useTranslate()
      const title = cell.getValue<string>()
      const { onOpenWorkflow, onRerunWorkflow, prompt } = row.original
      return (
        <div className='flex items-center gap-3'>
          <Button
            onClick={onOpenWorkflow}
            unstyled
            className='max-w-80 cursor-pointer truncate px-0 text-left font-semibold normal-case hover:text-mango-300'
            title={title}
          >
            {title || '-'}
          </Button>
          <TooltipIconButton
            disabled={typeof prompt !== 'string'}
            onClick={onRerunWorkflow}
            title={t('history.workflowsTable.a11y.rerun')}
            keyName='history.workflowsTable.a11y.rerun'
          >
            <RerunIcon className='text-neutral-150' />
          </TooltipIconButton>
        </div>
      )
    },
  },
  {
    accessorKey: 'recentTurnStartTimeEpoch',
    enableColumnFilter: false,
    header: ({ column }) => (
      <DataTableColumnHeader
        titleKey='history.workflowsTable.headers.recentTurnStartTimeEpoch'
        column={column}
        columnSort={{
          tooltip: {
            default: 'history.sort.tooltip.recentTurnStartTimeEpoch.default',
            desc: 'history.sort.tooltip.recentTurnStartTimeEpoch.desc',
          },
        }}
      >
        <T keyName='history.workflowsTable.headers.recentTurnStartTimeEpoch' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue()
      if (typeof value === 'number') return dayjs(value * 1000).format('MM/DD/YYYY')
      //return '--/--/--'
    },
  },
  {
    accessorKey: 'status',
    enableColumnFilter: true,
    enableSorting: true,
    header: ({ column }) => (
      <DataTableColumnHeader
        titleKey='history.workflowsTable.headers.status'
        column={column}
        columnSort={{
          tooltip: {
            default: 'history.sort.tooltip.status.default',
            desc: 'history.sort.tooltip.status.desc',
          },
        }}
        FilterComp={CHStatusColumnFilter}
      >
        <T keyName='history.workflowsTable.headers.status' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const status = cell.getValue()
      if (workflowStatusTypeGuard(status)) {
        return <HistoryWorkflowStatus status={status} />
      }
    },
  },
  {
    header: () => <T keyName='history.workflowsTable.headers.actions' />,
    size: 20,
    accessorKey: 'options',
    cell: ({ row }) => (
      <div className='flex w-full items-center justify-center'>
        <ChatOptionsMenu
          workflowId={row.original.workflowId}
          workflowTitle={row.original.title ?? ''}
        />
      </div>
    ),
  },
  {
    header: '',
    size: 20,
    accessorKey: 'others',
    cell: ({ row }) => {
      return <ScheduleCard scheduleId={row.original.scheduleId} />
    },
  },
]

function ChatHistoryTable() {
  const { addTab, getTabPositionMetadata } = useSplitView()
  const { setStoredValue } = usePromptLocalStorage('home')

  // Use persistent pagination and sorting state
  const [pagination, setPagination] = usePersistentPagination({
    key: 'history-table-pagination',
    initialPagination: {
      pageIndex: 0,
      pageSize: DEFAULT_PAGINATION_OPTIONS.defaultPageSize,
    },
  })

  const [sorting, setSorting] = usePersistentSorting<SortingState>({
    key: 'history-table-sorting',
    initialSorting: [{ id: 'recentTurnStartTimeEpoch', desc: true }],
  })

  const [columnFilters, setColumnFilters] = usePersistentColumnFilters<ColumnFiltersState>({
    key: 'history-table-column-filters',
    initialColumnFilters: [],
  })

  const getColumnFilterValue = React.useCallback(
    ({ id }: { id: string }) => {
      return columnFilters.find((filter) => filter.id === id)?.value as string
    },
    [columnFilters],
  )
  const getSortValue = React.useCallback(() => {
    return sorting.map((sort) => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`).join(',')
  }, [sorting])

  const workflowsSearchParams: UseAllWorkflowsSearchParams = useMemo(() => {
    return {
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      sort: getSortValue(),
      statuses: getColumnFilterValue({ id: 'status' }),
    }
  }, [pagination, getSortValue, getColumnFilterValue])

  const workflowsQuery = useAllWorkflowsQuery({
    searchParams: workflowsSearchParams,
    queryOptions: {
      refetchInterval: (query) => {
        let isWorkflowLoading = false
        query.state?.data?.data?.forEach((w) => {
          if (isWorkflowStatusLoading(w?.status)) isWorkflowLoading = true
        })
        return isWorkflowLoading ? 2000 : false
      },
    },
  })

  const resetPageIndex = () => {
    setPagination((state) => ({
      ...state,
      pageIndex: 0,
    }))
  }

  const getOppositePanelId = React.useCallback(() => {
    const position = getTabPositionMetadata({ tabType: TabTypeStatic.History })
    return position?.panelId === TabsPanelId.Right ? TabsPanelId.Left : TabsPanelId.Right
  }, [getTabPositionMetadata])

  const getChatIndex = React.useCallback(
    ({ workflowId }: { workflowId: string }) => {
      const position = getTabPositionMetadata({ tabType: TAB_TYPE_CHAT, tabId: workflowId })
      return position?.index ?? 0
    },
    [getTabPositionMetadata],
  )

  const tableData = React.useMemo(
    () =>
      workflowsQuery.data?.data.map((w) => ({
        ...w,
        recentTurnStartTimeEpoch: w.recentTurnStartTimeEpoch ?? w.startEpoch,
        onOpenWorkflow: () => {
          addTab({
            tab: createWorkflowTab({ workflowId: w.workflowId, title: w.title }),
            index: getChatIndex({ workflowId: w.workflowId }),
            panelId: getOppositePanelId(),
          })
        },
        onRerunWorkflow: () => {
          if (w.prompt && typeof w.prompt === 'string') {
            setStoredValue(w.prompt)
            addTab({ tab: createStaticTab(TabTypeStatic.NewChat), panelId: getOppositePanelId() })
          }
        },
      })) ?? [],
    [workflowsQuery.data?.data, addTab, getOppositePanelId],
  )

  const table = useReactTable<ChatHistoryTableDef>({
    data: tableData,
    columns: CHAT_HISTORY_COLUMN_DEFS,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    sortDescFirst: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: true,
    rowCount: workflowsQuery.data?.pagination.totalItems,
    pageCount: workflowsQuery.data?.pagination.totalPages,
    isMultiSortEvent: () => true,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = workflowsQuery.status === 'pending'
  const isTableLoading = isInitialLoading && workflowsQuery.isFetching && rows.length === 0

  React.useEffect(() => {
    if (workflowsQuery.data?.data?.length === 0 && pagination.pageIndex > 0) {
      resetPageIndex()
      return
    }
    if (!workflowsQuery.data) return
  }, [workflowsQuery.data])

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header>
        <div>
          <PageTemplate.H4 className='font-normal text-neutral-500'>
            <T keyName={'history.subtitle'} />
          </PageTemplate.H4>
        </div>
      </PageTemplate.Header>
      <Table>
        <TableHeader className={cn('bg-content z-10 bg-neutral-25')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: `${header.getSize()}px` }}
                  className={cn(header.id === 'options' && 'text-center')}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && (
            <TableSkeletonRows
              cellCount={CHAT_HISTORY_COLUMN_DEFS.length}
              className='bg-neutral-white'
            />
          )}
          {workflowsQuery.status === 'error' && (
            <TableErrorRow colSpan={CHAT_HISTORY_COLUMN_DEFS.length} />
          )}
          {workflowsQuery.status === 'success' &&
            rows.length > 0 &&
            rows.map((row) => (
              <TableRow key={row.id} className='h-[72px] bg-neutral-white'>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    data-state={
                      (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {workflowsQuery.status === 'success' &&
            rows?.length === 0 &&
            !workflowsQuery.isFetching && (
              <TableRow>
                <TableCell colSpan={CHAT_HISTORY_COLUMN_DEFS.length} className='h-24 text-center'>
                  No results.
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </Table>
      {workflowsQuery.status === 'success' && (
        <div className='mb-6'>
          <PaginationControls
            table={table}
            isTableLoading={isTableLoading}
            selectedPageIndex={pagination.pageIndex}
          />
        </div>
      )}
    </div>
  )
}

// 👇 helpers and type guards

const workflowStatusTypeGuard = (status?: unknown): status is WorkflowStatus => {
  if (!status) return false

  return Object.values(WorkflowStatus).includes(status as WorkflowStatus)
}

// 👇 workflow statuses

export const WORKFLOW_STATUS: Record<
  WorkflowStatus,
  { icon: React.ReactNode; name: string; color: 'error' | 'mango' | 'success' | 'neutral' }
> = {
  [WorkflowStatus.Queued]: {
    icon: <CircleDashedIcon />,
    name: 'In Progress',
    color: 'mango',
  },
  [WorkflowStatus.InProgress]: {
    icon: <CircleDashedIcon />,
    name: 'In Progress',
    color: 'mango',
  },
  [WorkflowStatus.Planning]: {
    icon: <CircleDashedIcon />,
    name: 'In Progress',
    color: 'mango',
  },
  [WorkflowStatus.ValidationError]: {
    icon: <CircleAlertIcon />,
    name: 'Failed',
    color: 'error',
  },
  [WorkflowStatus.Failure]: {
    icon: <CircleAlertIcon />,
    name: 'Failed',
    color: 'error',
  },
  [WorkflowStatus.Success]: {
    icon: <CircleCheckIcon />,
    name: 'Completed',
    color: 'success',
  },
  [WorkflowStatus.Cancelled]: {
    icon: <XCircleIcon />,
    name: 'Cancelled',
    color: 'neutral',
  },
}

function HistoryWorkflowStatus({
  status,
  showIcon = true,
}: Readonly<{ status: WorkflowStatus; showIcon?: boolean }>) {
  const { name, color, icon } = WORKFLOW_STATUS[status]
  return (
    <div
      className={cn(
        'flex h-6 w-fit items-center gap-1.5 whitespace-nowrap rounded pl-2 pr-3 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0',
        color === 'success' && 'bg-success-50 text-success-300 [&>svg]:text-success-300',
        color === 'neutral' && 'bg-neutral-50 text-black/60 [&>svg]:text-black/60',
        color === 'mango' && 'bg-mango-50 text-mango-300 [&>svg]:text-mango-300',
        color === 'error' && 'bg-grape-50 text-grape-300 [&>svg]:text-grape-300',
      )}
    >
      {showIcon && icon}
      <p className='text-xs font-normal'>{name}</p>
    </div>
  )
}

export { ChatHistoryTable, HistoryWorkflowStatus }
